import React from 'react'
import {
  LifeEventType,
  type LoanDocument,
  type SolarDoc,
  type NameMismatchDetails
} from '@oneethos/shared'
import { MultiFileUpload } from '../../components/forms/multi-file-upload'

const LIFE_EVENT_LABELS: Record<LifeEventType, { label: string; docPrompt: string }> = {
  [LifeEventType.RecentlyMarried]: {
    label: 'Recently married',
    docPrompt: 'Please upload your marriage certificate.'
  },
  [LifeEventType.RecentlyDivorced]: {
    label: 'Recently divorced',
    docPrompt: 'Please upload your divorce decree.'
  },
  [LifeEventType.NameChange]: {
    label: 'Name change',
    docPrompt: 'Please upload proof of your name change.'
  },
  [LifeEventType.Other]: {
    label: 'Other',
    docPrompt: 'Please upload proof of the change.'
  }
}

const NameMismatchDetailsForm = ({
  nameMismatchDetails,
  borrowerKey,
  onChange
}: {
  nameMismatchDetails: NameMismatchDetails
  borrowerKey: string // unique per borrower to avoid duplicate input name/id collisions
  onChange: (updated: NameMismatchDetails) => void
}) => {
  return (
    <div className="mt-3">
      <div className="form-group">
        <label htmlFor={`lifeEvent-${borrowerKey}`} className="form-label">
          Has your name changed due to a life event?
        </label>
        <div className="d-flex gap-5">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name={`lifeEvent-${borrowerKey}`}
              id={`lifeEventYes-${borrowerKey}`}
              value="yes"
              checked={nameMismatchDetails?.hadLifeEvent === true}
              onChange={() => onChange({ hadLifeEvent: true, lifeEventType: null, supportingDocument: null })}
            />
            <label className="form-check-label" htmlFor={`lifeEventYes-${borrowerKey}`}>Yes</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name={`lifeEvent-${borrowerKey}`}
              id={`lifeEventNo-${borrowerKey}`}
              value="no"
              checked={nameMismatchDetails?.hadLifeEvent === false}
              onChange={() => onChange({ hadLifeEvent: false, lifeEventType: null, supportingDocument: null })}
            />
            <label className="form-check-label" htmlFor={`lifeEventNo-${borrowerKey}`}>No</label>
          </div>
        </div>
      </div>

      {nameMismatchDetails?.hadLifeEvent === true && (
        <div className="form-group mt-3">
          <label htmlFor={`lifeEventType-${borrowerKey}`} className="form-label">
            Which of the following applies?
          </label>
          <select
            className="form-select"
            id={`lifeEventType-${borrowerKey}`}
            value={nameMismatchDetails.lifeEventType ?? ''}
            onChange={(e) =>
              onChange({
                ...nameMismatchDetails,
                lifeEventType: e.target.value as LifeEventType,
                supportingDocument: null
              })
            }
          >
            <option value="">(make selection)</option>
            {(Object.values(LifeEventType) as LifeEventType[]).map((eventType) => (
              <option key={eventType} value={eventType}>
                {LIFE_EVENT_LABELS[eventType].label}
              </option>
            ))}
          </select>

          {nameMismatchDetails.lifeEventType && (
            <div className="mt-3">
              <p>{LIFE_EVENT_LABELS[nameMismatchDetails.lifeEventType].docPrompt}</p>
              <MultiFileUpload
                docs={(nameMismatchDetails.supportingDocument ?? []) as (File & SolarDoc)[]}
                numRequired={1}
                onChange={(files) =>
                  onChange({
                    ...nameMismatchDetails,
                    supportingDocument: files.length
                      ? [files[0] as unknown as LoanDocument]
                      : null
                  })
                }
              />
            </div>
          )}
        </div>
      )}

      {nameMismatchDetails?.hadLifeEvent === false && (
        <p className="text-muted small mt-2 alert alert-info">
          No additional documentation required for the name difference.
        </p>
      )}
    </div>
  )
}

export default NameMismatchDetailsForm